import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiRequestError } from '../lib/api.js';
import { useSendMessage, useThread } from '../lib/hooks.js';
import { Button, EmptyState, Input, Skeleton } from '../ui/index.js';
import { useToast } from '../ui/toast.js';
import { relTime } from '../lib/format.js';

export function MessageThread({ customerId }: { customerId: string }) {
  const { data, isLoading } = useThread(customerId);
  const send = useSendMessage(customerId);
  const toast = useToast();
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [channel, setChannel] = useState<'sms' | 'note'>('sms');

  const submit = async () => {
    if (!text.trim()) return;
    try {
      await send.mutateAsync({ channel, body: text });
      setText('');
    } catch (err) {
      toast.push(err instanceof ApiRequestError ? err.message : 'Send failed', 'error');
    }
  };

  const simulateInbound = async () => {
    try {
      await api.post(`/customers/${customerId}/messages/simulate-inbound`, { body: 'Hi! Is the technician still coming today?' });
      qc.invalidateQueries({ queryKey: ['thread', customerId] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      toast.push('Simulated an inbound text', 'success');
    } catch (err) {
      toast.push(err instanceof ApiRequestError ? err.message : 'Could not simulate', 'error');
    }
  };

  const messages = data?.items ?? [];

  return (
    <>
      {isLoading ? (
        <div className="thread"><Skeleton h={40} w="60%" /><Skeleton h={40} w="50%" style={{ alignSelf: 'flex-end' }} /></div>
      ) : messages.length === 0 ? (
        <div style={{ padding: 'var(--sp-4)' }}><EmptyState icon="☏" title="No messages yet">Send a text or leave an internal note.</EmptyState></div>
      ) : (
        <div className="thread">
          {messages.map((m) => {
            const cls = m.channel === 'note' ? 'note' : m.direction === 'inbound' ? 'in' : 'out';
            return (
              <div key={m.id} className={`bubble ${cls}`}>
                {m.channel === 'call' ? `📞 Call (${m.durationSec ?? 0}s)` : m.body}
                <div className="meta">{m.channel === 'note' ? 'Internal note' : m.channel.toUpperCase()} · {m.direction} · {relTime(m.createdAt)}{m.delivery && m.direction === 'outbound' ? ` · ${m.delivery}` : ''}</div>
              </div>
            );
          })}
        </div>
      )}
      <div className="composer">
        <select className="select" value={channel} onChange={(e) => setChannel(e.target.value as 'sms' | 'note')} style={{ width: 96 }}>
          <option value="sms">Text</option>
          <option value="note">Note</option>
        </select>
        <Input className="grow" placeholder={channel === 'sms' ? 'Type a text…' : 'Add an internal note…'} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
        <Button loading={send.isPending} onClick={submit}>Send</Button>
        <Button variant="ghost" size="sm" onClick={simulateInbound} title="Dev: simulate an inbound text">⇤ Sim</Button>
      </div>
    </>
  );
}
